import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useBranding } from "@/lib/branding";
import axios from "axios";
import { X, Calendar, ClipboardList, Search, Phone, MessageSquareText, Send, ArrowLeft, Sparkles } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = "dt_welcome_dismissed_v1";
const AUTO_OPEN_MS = 2200;
const SESSION_KEY = "dt_assist_session";

// Pages where the welcome bubble should be hidden entirely.
const HIDDEN_PATHS = ["/intake", "/amend"];

export default function WelcomeBubble() {
  const { branding } = useBranding();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [greetingShown, setGreetingShown] = useState(false);
  const [bio, setBio] = useState(null);
  const [visible, setVisible] = useState(false);
  // Chat state
  const [mode, setMode] = useState("menu"); // "menu" | "chat"
  const [messages, setMessages] = useState([]); // {role, content}
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef();

  useEffect(() => {
    axios.get(`${API_URL}/api/instructor-bio`).then((r) => setBio(r.data)).catch(() => {});
    let dismissed = false;
    try { dismissed = window.sessionStorage.getItem(DISMISS_KEY) === "1"; } catch (_) {}
    const t1 = setTimeout(() => setVisible(true), 600);
    let t2;
    if (!dismissed) t2 = setTimeout(() => setGreetingShown(true), AUTO_OPEN_MS);
    return () => { clearTimeout(t1); if (t2) clearTimeout(t2); };
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const photo = bio?.photo_url
    ? (bio.photo_url.startsWith("http") ? bio.photo_url : `${API_URL}${bio.photo_url}`)
    : null;
  const firstName = (bio?.name || "").split(" ")[0] || "Di'Meka";
  const phone = branding?.phone;
  const chatEnabled = !!(branding?.tawk_property_id && branding?.tawk_widget_id);

  const dismissGreeting = () => {
    setGreetingShown(false);
    try { window.sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_) {}
  };

  const openTawk = () => {
    try {
      if (window.Tawk_API?.maximize) window.Tawk_API.maximize();
    } catch (_) {}
  };

  const startChat = () => {
    if (messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: `Hi! I'm ${firstName}'s AI helper — I can answer questions about pricing, appointments, refund policies, and how the whole process works. What's on your mind?`,
      }]);
    }
    setMode("chat");
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setDraft("");
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    try {
      let sessionId = "";
      try { sessionId = window.sessionStorage.getItem(SESSION_KEY) || ""; } catch (_) {}
      const r = await axios.post(`${API_URL}/api/assistant/message`, {
        message: text,
        session_id: sessionId,
        history: nextMessages.slice(-6),
      }, { timeout: 30000 });
      if (r.data.session_id) {
        try { window.sessionStorage.setItem(SESSION_KEY, r.data.session_id); } catch (_) {}
      }
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, escalate: r.data.escalate, book_prompt: r.data.book_prompt }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "I couldn't reach my brain just now — try again in a moment, or use the buttons below.";
      setMessages((m) => [...m, { role: "assistant", content: msg, error: true }]);
    } finally {
      setSending(false);
    }
  };

  if (!visible) return null;
  if (HIDDEN_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) return null;

  return (
    <div
      className="fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6 flex flex-col items-end gap-3"
      data-testid="welcome-bubble"
    >
      {greetingShown && !open && (
        <div
          className="max-w-[260px] bg-card border border-border rounded-2xl rounded-br-sm shadow-lg p-3 pr-8 relative animate-in fade-in slide-in-from-bottom-2"
          role="dialog"
          data-testid="welcome-greeting"
        >
          <button
            onClick={dismissGreeting}
            className="absolute top-1.5 right-1.5 p-1 rounded-full hover:bg-secondary"
            aria-label="Dismiss greeting"
            data-testid="welcome-greeting-dismiss"
          >
            <X size={11} />
          </button>
          <div className="label-caps text-[9px] text-primary mb-0.5">{firstName}</div>
          <div className="text-sm leading-snug">Welcome — how can we assist today?</div>
        </div>
      )}

      {open && (
        <div
          className="w-[340px] max-w-[calc(100vw-2rem)] max-h-[80vh] bg-card border border-border rounded-2xl rounded-br-sm shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 flex flex-col"
          role="dialog"
          data-testid="welcome-card"
        >
          <div className="p-3 bg-primary text-primary-foreground flex items-center gap-3">
            {mode === "chat" && (
              <button
                onClick={() => setMode("menu")}
                className="p-1 rounded-full hover:bg-primary-foreground/10"
                aria-label="Back"
                data-testid="welcome-chat-back"
              >
                <ArrowLeft size={14} />
              </button>
            )}
            {photo ? (
              <img src={photo} alt="" className="h-9 w-9 rounded-full object-cover border-2 border-primary-foreground/40 shrink-0" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-primary-foreground/20 grid place-items-center font-serif shrink-0">{firstName[0]}</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs opacity-80 leading-tight">
                {mode === "chat" ? <span className="flex items-center gap-1"><Sparkles size={10} /> AI helper for {firstName}</span> : <>Hi, I'm</>}
              </div>
              <div className="font-serif text-lg leading-tight truncate">{firstName}</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full hover:bg-primary-foreground/10"
              aria-label="Close welcome"
              data-testid="welcome-card-close"
            >
              <X size={14} />
            </button>
          </div>

          {mode === "menu" ? (
            <div className="p-4 space-y-2 overflow-y-auto">
              <div className="text-sm text-muted-foreground mb-2">Welcome — how can we assist today?</div>
              <button
                type="button"
                onClick={startChat}
                className="w-full flex items-center gap-2 text-sm border border-primary bg-primary/5 rounded-md px-3 py-2 hover:bg-primary/10 transition-colors text-left"
                data-testid="welcome-link-ai"
              >
                <span className="h-6 w-6 grid place-items-center rounded bg-primary text-primary-foreground shrink-0"><Sparkles size={12} /></span>
                <span className="flex-1">Ask my AI helper (instant, 24/7)</span>
              </button>
              {chatEnabled && (
                <button
                  type="button"
                  onClick={() => { openTawk(); setOpen(false); }}
                  className="w-full flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2 hover:border-primary transition-colors text-left"
                  data-testid="welcome-link-chat"
                >
                  <span className="h-6 w-6 grid place-items-center rounded bg-primary/10 text-primary shrink-0"><MessageSquareText size={12} /></span>
                  <span className="flex-1">Send {firstName} a message</span>
                </button>
              )}
              <QuickLink to="/book" icon={<Calendar size={14} />} label="Book an appointment" testid="welcome-link-book" />
              <QuickLink to="/intake" icon={<ClipboardList size={14} />} label="Start client intake" testid="welcome-link-intake" />
              <QuickLink to="/client-portal" icon={<Search size={14} />} label="Check my refund status" testid="welcome-link-portal" />
              {phone && (
                <a
                  href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
                  className="flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2 hover:border-primary transition-colors"
                  data-testid="welcome-link-call"
                >
                  <span className="h-6 w-6 grid place-items-center rounded bg-primary/10 text-primary shrink-0"><Phone size={12} /></span>
                  <span className="flex-1">Call {phone}</span>
                </a>
              )}
            </div>
          ) : (
            <>
              <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="welcome-chat-log">
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-sm ml-auto"
                        : m.error
                          ? "bg-destructive/10 text-destructive border border-destructive/30 rounded-bl-sm"
                          : "bg-secondary/50 rounded-bl-sm"
                    }`}
                    data-testid={`welcome-chat-msg-${m.role}-${i}`}
                  >
                    {m.content}
                    {m.book_prompt && (
                      <Link
                        to="/book"
                        onClick={() => setOpen(false)}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md px-2.5 py-1.5 hover:bg-primary/90 transition-colors"
                        data-testid={`welcome-chat-book-${i}`}
                      >
                        <Calendar size={12} /> Pick a time →
                      </Link>
                    )}
                    {m.escalate && chatEnabled && (
                      <button
                        onClick={() => { openTawk(); setOpen(false); }}
                        className="block mt-2 text-xs font-medium underline text-primary"
                        data-testid={`welcome-chat-escalate-${i}`}
                      >
                        → Send {firstName} a message
                      </button>
                    )}
                  </div>
                ))}
                {sending && (
                  <div className="max-w-[85%] rounded-2xl rounded-bl-sm px-3 py-2 text-sm bg-secondary/50">
                    <span className="inline-flex gap-1">
                      <span className="h-1.5 w-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="h-1.5 w-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                      <span className="h-1.5 w-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                    </span>
                  </div>
                )}
              </div>
              <div className="p-2 border-t border-border flex gap-2 items-end">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Ask about pricing, hours, refund status…"
                  rows={1}
                  className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary max-h-24"
                  data-testid="welcome-chat-input"
                  disabled={sending}
                />
                <button
                  onClick={send}
                  disabled={sending || !draft.trim()}
                  className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center disabled:opacity-40"
                  aria-label="Send"
                  data-testid="welcome-chat-send"
                >
                  <Send size={14} />
                </button>
              </div>
              <div className="px-3 pb-2 text-[10px] text-muted-foreground text-center">
                AI helper — not a real person. For complex situations, book or message {firstName}.
              </div>
            </>
          )}
        </div>
      )}

      <button
        onClick={() => { setOpen((o) => !o); setGreetingShown(false); dismissGreeting(); }}
        className="relative h-14 w-14 sm:h-16 sm:w-16 rounded-full shadow-lg border-2 border-card overflow-hidden hover:scale-105 transition-transform"
        aria-label="Chat with your preparer"
        data-testid="welcome-avatar"
      >
        {photo ? (
          <img src={photo} alt="Welcome" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-primary text-primary-foreground grid place-items-center font-serif text-xl">{firstName[0]}</div>
        )}
        {!open && (
          <span className="absolute top-0.5 right-0.5 h-3 w-3 rounded-full bg-accent border-2 border-card animate-pulse" aria-hidden />
        )}
      </button>
    </div>
  );
}

const QuickLink = ({ to, icon, label, testid }) => (
  <Link
    to={to}
    className="flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2 hover:border-primary transition-colors"
    data-testid={testid}
  >
    <span className="h-6 w-6 grid place-items-center rounded bg-primary/10 text-primary shrink-0">{icon}</span>
    <span className="flex-1">{label}</span>
  </Link>
);
